import React, { FC, memo, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useMedia } from "react-use";

import { IEditableSectionRenderProps } from "@/components/EditableSection";
import { Header } from "@/components/Header";
import { Text } from "@/components/Text";
import { UserContext } from "@/components/User";
import { MEDIA_SIZE_MD } from "@/core/media";
import { Flex } from "@/eworkitComponents";
import { IConsultantProfileSectionProps } from "@/views/components/ConsultantProfile/Sections/types";
import { ModalButton } from "@/views/components/Modal";
import { ResendInvitationButton } from "@/views/ConsultantProfiles/Detail/SettingsSection/ResendInvitationButton";
import { UnlinkModal } from "@/views/ConsultantProfiles/Detail/SettingsSection/UnlinkModal";

import { DeleteModal } from "./DeleteModal";
import { SendInvitationButton } from "./SendInvitationButton";
import { SettingsSectionFormik } from "./SettingsSectionEdit";
import { SettingsSectionReadOnly } from "./SettingsSectionReadOnly";

import "./_settings-section.scss";

interface Props extends IConsultantProfileSectionProps, IEditableSectionRenderProps {
  isAdmin: boolean;
  isManager: boolean;
  isOwner: boolean;
}

export const SettingsSection: FC<Props> = memo(({ record, editableProps, onUpdate, isAdmin, isManager, isOwner }) => {
  const User = useContext(UserContext);
  const { t } = useTranslation(["session", "common"]);
  const isMediumScreen = useMedia(MEDIA_SIZE_MD);
  const baseClass = "consultant-profile-settings";
  const role = record.companyRole || "PRIVATE_PERSON";
  const isPrivate = !User.contextCompany;

  const canManageCompanyProfile = isAdmin || (isManager && record.companyRole === "CONSULTANT");
  const showDeleteProfile = isPrivate || (!isOwner && canManageCompanyProfile);
  const showResendInvitation = canManageCompanyProfile && record.status === "PENDING";
  const showSendInvitation = canManageCompanyProfile && record.status === "NOT_INVITED";
  const showUnlinkUser = !isOwner && canManageCompanyProfile && record.status !== "NOT_INVITED";

  return (
    <Flex grow={1} direction="column" innerMargin="md">
      <Header size="sm" weight="bold" color="black">
        {t("CONSULTANT_PROFILES.DETAIL.SETTINGS.SECTION_HEADER")}
      </Header>

      {editableProps.editMode ? (
        <SettingsSectionFormik
          source={record}
          initialValues={{ companyRole: record.companyRole, searchable: record.settings.searchable }}
          onUpdate={onUpdate}
          onCancel={editableProps.handleCancelClick}
          editableProps={editableProps}
          isOwner={isOwner}
          isAdmin={isAdmin}
          isManager={isManager}
          role={role}
        />
      ) : (
        <SettingsSectionReadOnly searchable={record.settings.searchable} role={role} record={record} />
      )}

      <Flex direction={isMediumScreen ? "column" : "row"} innerMargin="md">
        {showDeleteProfile && (
          <Flex width={{ md: 12, sm: 6 }}>
            <ModalButton
              openable={DeleteModal}
              openableProps={{ size: "md", payload: record }}
              variant="link-danger"
              icon="trash"
              className={`${baseClass}__delete-button`}>
              <Text>
                {isPrivate
                  ? t("CONSULTANT_PROFILES.DETAIL.ACTIONS_PROFILE_DELETE")
                  : t("CONSULTANT_PROFILES.DETAIL.ACTIONS_PROFILE_DEACTIVATE")}
              </Text>
            </ModalButton>
          </Flex>
        )}

        <Flex width={{ md: 12, sm: 6 }} direction="column" innerMargin="sm">
          {(showResendInvitation || showSendInvitation) && (
            <Flex>
              {showResendInvitation && <ResendInvitationButton record={record} baseClass={baseClass} />}
              {showSendInvitation && <SendInvitationButton record={record} onUpdate={onUpdate} baseClass={baseClass} />}
            </Flex>
          )}
          {showUnlinkUser && (
            <Flex>
              <ModalButton
                openable={UnlinkModal}
                openableProps={{ size: "md", payload: record }}
                variant="link"
                icon="link"
                className={`${baseClass}__unlink-button`}>
                <Text>{t("CONSULTANT_PROFILES.DETAIL.ACTIONS_USER_UNLINK")}</Text>
              </ModalButton>
            </Flex>
          )}
        </Flex>
      </Flex>
    </Flex>
  );
});
